define('lib/Podio.Event',['require','underscore'],function(require) {
  var _ = require('underscore');

  var types = {
    init: 'init',
    onAfterBundleLoaded: 'onAfterBundleLoaded',
    unload: 'unload',
    scroll: 'scroll',
    resize: 'resize',
    taskComplete: 'taskComplete',
    modalCompleted: 'modalCompleted',
    modalAborted: 'modalAborted',
    delayedContentReady: 'delayedContentReady',
    beforeChatPaneOpened: 'beforeChatPaneOpened',
    chatPaneOpened: 'chatPaneOpened',
    beforeChatPaneClosed: 'beforeChatPaneClosed',
    chatPaneClosed: 'chatPaneClosed',
    featureTourClosed: 'featureTourClosed',
    overlayClosed: 'overlayClosed',
    overlayOpened: 'overlayOpened',
    onTransportGlobalSuccess: 'onTransportGlobalSuccess',
    onTransportGlobalError: 'onTransportGlobalError',
    onChatNotificationClicked: 'onChatNotificationClicked'
  };

  var domEvents = {};

  domEvents[types.unload] = { eventName: 'beforeunload' };
  domEvents[types.scroll] = { eventName: 'scroll' };
  domEvents[types.resize] = { eventName: 'resize' };

  var events = {};
  var delayedContentReady = false;

  /**
   * The bind function lets you bind an event handler to an event of a certain type with an optional scope.
   * Native DOM events are bound to a DOM event handler only once, and all event handler callbacks are
   * run synchronously from there.
   *
   * @param {String} type The event type. Can be a DOM event or any custom type. Use Podio.Event.Types[type] from the caller
   * @param {Function} fn The event handler callback. Called each time an event of the given type triggers
   * @param {Object} [scope] An optional scope to call the event handler callback in. Also useful for unbinding all handlers with a certain scope in `unbind`
   *
   * @throws {object} Error object if arguments are missing or of the wrong type
   *
   * @returns {Boolean|Function} Returns the callback function passed in as the `fn` argument. Returns `false` in case of legacy `init` and `delayedContentReady`events
   *
   * @example <pre>PodioEvent.bind('myEvent', function (data) { alert(data); })</pre>
   * @example <pre>PodioEvent.bind('myEvent', function (data) { this.handle(data); }, this)</pre>
   */
  function bind(type, fn, scope) {
    if (typeof type !== 'string') {
      throw new Error('Podio.Event.bind(type, fn, [scope]), Argument `type` must be a String');
    }
    if (typeof fn !== 'function') {
      throw new Error('Podio.Event.bind(type, fn, [scope]), Argument `fn` must be a Function');
    }

    // Legacy support
    // These would previously be fired after the documents domReady event
    // Now we're loading scripts asynchronously, the dom is always ready
    // FIXME: Clean out all Podio.Event bind calls to Podio.Event.Types.[init | delayedContentReady]
    if ((type === types.init) || (type === types.delayedContentReady && delayedContentReady)) {
      if (typeof scope !== 'undefined') {
        fn.call(scope);
      } else {
        fn();
      }
      return false;
    }

    // Create an event object array for the event type is none exists
    if (!Array.isArray(events[type])) {
      events[type] = [];
    }

    events[type].push({
      scope: scope,
      callback: fn
    });

    // For DOM mapped events we're attaching a single event handler that triggers the Podio.Event
    // Only do this for the first binding of a type to reduce the amount of dom event listeners
    if (typeof domEvents[type] !== 'undefined' && typeof domEvents[type].eventHandler !== 'function') {
      domEvents[type].eventHandler = trigger.bind(window, type);
      window.addEventListener(domEvents[type].eventName, domEvents[type].eventHandler);
    }

    return fn;
  }

  /**
   * The unbind function lets you unbind a previously bound event handler, either with type and event handler, type and scope, or scope alone.
   * Using type and event handler only the single event handler will be unbound.
   * Using type and scope all event handlers of that type and scope will be unbound.
   * Using scope alone will unbind any event handler of any type from that specific scope.
   *
   * @param {String} type The event type
   * @param {Function} fn The event handler callback
   * @param {Object} scope The scope an event was bound to
   *
   * @throws {Object} Error object if arguments are missing or of the wrong type
   *
   * @returns {Undefined} No single return value makes sense
   *
   * @example Unbinding a known event type and handler <pre>PodioEvent.unbind('myEvent', PodioEvent.bind('myEvent', function (data) { alert(data); }))</pre>
   * @example Unbinding all event handlers of a specific type from a known scope <pre>PodioEvent.unbind('myEvent', undefined, this)</pre>
   * @example Unbinding all event handlers from a known scope <pre>PodioEvent.unbind(undefined, undefined, this)</pre>
   */
  function unbind(type, fn, scope) {
    if (typeof fn !== 'function') {
      if (typeof type === 'undefined') {
        if (typeof scope !== 'undefined') {
          return unbindScope(scope);
        } else {
          throw new Error('Podio.Event.bind(type, fn, [scope]), Missing type, fn and scope arguments');
        }
      } else {
        if (typeof scope !== 'undefined') {
          return unbindScopedType(type, scope);
        } else {
          throw new Error('Podio.Event.bind(type, fn, [scope]), Missing either fn or scope argument');
        }
      }
    }

    if (typeof type === 'undefined') {
      throw new Error('Podio.Event.bind(type, fn, [scope]), Missing type argument');
    }

    // From this point we should be guaranteed a type and a fn

    // No events of the specified type are registered
    if (!Array.isArray(events[type])) {
      return false;
    }

    // Find and remove the correct event objects
    events[type] = events[type].filter(function (eventObject) {
      return eventObject.callback !== fn;
    });

    // If there are no more listeners on a Podio.Event, clean up the dom event listeners
    if (events[type].length === 0 && typeof domEvents[type] !== 'undefined' && typeof domEvents[type].eventHandler === 'function') {
      window.removeEventListener(domEvents[type].eventName, domEvents[type].eventHandler);
      delete domEvents[type].eventHandler;
    }
  }

  /**
   * The trigger function lets you trigger previously bound event handler callbacks of the triggered type.
   * Native DOM events are also exposed through this interface, enabling you to trigger `resize` or `scroll` handlers without triggering a native dom event.
   *
   * @param {String} type The event type
   * @param [data] Any custom data you'd like to pass into callback functions
   */
  function trigger(type) {
    if (type === 'scroll') {
      _scrollTrigger.apply(undefined, arguments);
    } else if (type === 'resize') {
      _resizeTrigger.apply(undefined, arguments);
    } else {
      _trigger.apply(undefined, arguments);
    }
  }

  function _trigger(type) {
    if (Array.isArray(events[type])) {
      var args = arguments;
      events[type].forEach(function (eventObject) {
        eventObject.callback.apply(eventObject.scope || window, args);
      });
    }

    if (type === types.delayedContentReady) {
      delayedContentReady = true;
    }
  }

  var _scrollTrigger = _.throttle(_trigger, 32);
  var _resizeTrigger = _.throttle(_trigger, 32);

  // Internal function, not publicly exposed
  // Unbinds all events of a specific type from a specific scope
  function unbindScopedType(type, scope) {
    if (Array.isArray(events[type])) {
      events[type].forEach(function (eventObject) {
        if (eventObject.scope === scope) {
          unbind(type, eventObject.callback);
        }
      });
    }
  }

  // Internal function, not publicly exposed
  // Unbinds all events of a specific scope
  function unbindScope(scope) {
    Object.keys(events).forEach(function (type) {
      unbindScopedType(type, scope);
    });
  }

  // Clean up bound events when unloading the page
  bind(types.unload, function unbindAll() {
    Object.keys(events).forEach(function (type) {
      events[type].forEach(function (eventObject) {
        unbind(type, eventObject.callback);
      });
    });
  });

  return {
    bind: bind,
    unbind: unbind,
    trigger: trigger,
    Types: types
  };

});

